// Timer.tsx
import React, { FC, useEffect, useRef, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { RecordingState } from '../appointments/recording/RecordingState';
import { createUseStyles } from 'react-jss';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { APP_BACKGROUNDED, APP_FOREGROUNDED } from '../AppContainer';
import { isPlatform } from '../utils/isPlatform';

export type IStyleProps = {
    size?: 'small' | 'normal'
};

export type ITimerProps = IStyleProps & {
    onPause?: (seconds: number) => void;
};

type IStyleKeys = 'wrapper' | 'colon' | 'small';
const useStyles = createUseStyles<IStyleKeys, IStyleProps>({
    wrapper: {
        width: '100%',
        textAlign: 'center',

        '& > span': {
            display: 'inline-block',
            width: ({ size }) => size === 'small' ? 12 : 31
        }
    },

    colon: {
        width: 10,
        display: 'inline-block',
        position: 'relative',
        margin: '0 2px 5px 3px',
        verticalAlign: 'middle',
        '&::before, &::after': {
            content: '""',
            display: 'block',
            width: 8,
            height: 8,
            background: 'currentColor',
            borderRadius: '50%',
            position: 'absolute',
            left: 0
        },
        '&::before': {
            top: 2
        },
        '&::after': {
            bottom: 4
        }
    },

    small: {
        width: 5,
        '&::before, &::after': {
            width: 4,
            height: 4
        },
        '&::before': {
            top: 2
        },
        '&::after': {
            bottom: 0
        }
    }
});

export const Colon: FC<ITimerProps> = ({ size }) => {
    const classes = useStyles({ size });
    return (
        <div className={`${classes.colon} ${size === 'small' ? classes.small : ''}`} />
    );
};

export const Timer: FC<ITimerProps> = ({ size = 'normal', onPause }) => {
    const classes = useStyles({ size });

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    const eventBus = useInstance(BufferedEventBus);

    const [seconds, setSeconds] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const lastActiveRef = useRef<number | null>(null);

    useEffect(() => {
        if (recordingState.isPaused() && onPause) {
            onPause(seconds);
        }
    }, [recordingState.state]); // eslint-disable-line react-hooks/exhaustive-deps

    // Store the timestamp when timer starts or resumes
    useEffect(() => {
        if (!recordingState.isPaused() && recordingState.isRecording()) {
            if (lastActiveRef.current === null) {
                lastActiveRef.current = Date.now();
            }
            intervalRef.current = setInterval(() => {
                setSeconds(prev => prev + 1);
            }, 1000);
        } else if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            lastActiveRef.current = null;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [recordingState, recordingState.state]);

    // Listen for APP_FOREGROUNDED event to keep timer accurate
    useEffect(() => {
        if (isPlatform('capacitor')) {
            const handleAppForegrounded = () => {
                if (!recordingState.isPaused() && recordingState.isRecording()) {
                    if (lastActiveRef.current !== null) {
                        const now = Date.now();
                        const elapsed = Math.floor((now - lastActiveRef.current) / 1000);
                        if (elapsed > 0) {
                            setSeconds(prev => prev + elapsed);
                        }
                        lastActiveRef.current = now;
                    }
                }
            };

            const handleAppBackgrounded = () => {
                if (!recordingState.isPaused() && recordingState.isRecording()) {
                    // Store the timestamp when app is backgrounded
                    lastActiveRef.current = Date.now();
                }
            };

            const foreUnsub = eventBus.on(APP_FOREGROUNDED, handleAppForegrounded);
            const backUnsub = eventBus.on(APP_BACKGROUNDED, handleAppBackgrounded);

            return () => {
                foreUnsub();
                backUnsub();
            };
        }
    }, [eventBus, recordingState, recordingState.state]);

    const formatTime = (totalSeconds: number) => {
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;
        const pad = (n: number) => n.toString().padStart(2, '0');

        return (
            <span className={classes.wrapper}>
                {h > 0 ? <><span>{h}</span><Colon size={size} /></> : ''}
                <span>{pad(m).substring(0, 1)}</span><span>{pad(m).substring(1, 2)}</span><Colon size={size} />
                <span>{pad(s).substring(0, 1)}</span><span>{pad(s).substring(1, 2)}</span>
            </span>
        );
    };

    return formatTime(seconds);
};
